import React from "react";
import { motion } from "framer-motion";
import { Hammer, MessageSquare, Euro, Target } from "lucide-react";

const BLOCKS = [
    {
        icon: Hammer,
        title: "Spécialisés artisans",
        desc: "On ne fait pas de site pour tout le monde. On connaît votre métier, vos clients, vos urgences. Votre plombier de quartier n'a pas les mêmes besoins qu'un cabinet d'avocats.",
    },
    {
        icon: MessageSquare,
        title: "Pas de jargon",
        desc: "On vous parle clairement. Vous comprenez chaque étape, chaque ligne de devis, chaque choix technique. Si on emploie un mot compliqué, on l'explique.",
    },
    {
        icon: Euro,
        title: "Tarif transparent",
        desc: "Devis détaillé dès le premier rendez-vous. Pas de « ça dépend » ni de surcoûts découverts au dernier moment. Le prix annoncé est le prix final.",
    },
    {
        icon: Target,
        title: "Engagement résultat",
        desc: "On suit vos demandes de devis après la livraison. On n'arrête pas quand on encaisse. Votre site doit vous rapporter, sinon on corrige le tir.",
    },
];

export default function WhyUs() {
    return (
        <section
            id="agence"
            className="relative bg-[#F7F7F5] text-[#1A1A2E] py-24 md:py-32"
            data-testid="whyus-section"
        >
            <div className="max-w-7xl mx-auto px-4 md:px-8">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-28">
                        <span className="inline-flex items-center gap-2 rounded-full bg-[#1A1A2E]/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#1A1A2E]/60">
                            Pourquoi alva
                        </span>
                        <h2
                            className="mt-6 font-display font-bold tracking-tight text-balance"
                            style={{ fontSize: "clamp(32px, 5vw, 48px)", lineHeight: 1.02 }}
                            data-testid="whyus-title"
                        >
                            Pas de portfolio clinquant.
                            <span className="text-[#2B7BE4]"> Juste une méthode qui tient.</span>
                        </h2>
                        <p className="mt-6 text-base md:text-lg text-[#1A1A2E]/70 leading-relaxed">
                            Alva Digital est jeune. On préfère le reconnaître plutôt que d'inventer de faux témoignages.
                            Notre parole, c'est notre façon de travailler : claire, directe, orientée résultat.
                        </p>
                        <div className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-white border border-[#1A1A2E]/5 p-4 shadow-sm">
                            <div className="flex -space-x-2">
                                <span className="size-9 rounded-full bg-gradient-to-br from-[#2B7BE4] to-[#2B7BE4]" />
                                <span className="size-9 rounded-full bg-gradient-to-br from-[#1A2F6E] to-[#1A2F6E]" />
                            </div>
                            <div className="text-sm">
                                <div className="font-semibold text-[#1A1A2E]">Équipe parisienne</div>
                                <div className="text-[#1A1A2E]/50">Disponible en visio 5j/7</div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {BLOCKS.map((b, i) => (
                            <motion.article
                                key={b.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.6, delay: i * 0.08 }}
                                className={`group rounded-2xl bg-white border border-[#1A1A2E]/5 p-7 shadow-[0_8px_30px_rgba(26,26,46,0.04)] hover:shadow-[0_24px_60px_rgba(26,26,46,0.08)] transition-all duration-500 hover:-translate-y-1 ${
                                    i % 2 === 1 ? "md:translate-y-8" : ""
                                }`}
                                data-testid={`whyus-block-${i}`}
                            >
                                <div className="inline-flex size-11 items-center justify-center rounded-xl bg-[#2B7BE4] text-white">
                                    <b.icon size={20} strokeWidth={2.2} />
                                </div>
                                <h3 className="mt-6 font-display text-xl font-bold tracking-tight">
                                    {b.title}
                                </h3>
                                <p className="mt-3 text-[15px] text-[#1A1A2E]/70 leading-relaxed">
                                    {b.desc}
                                </p>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
